import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { UserMinus, AlertCircle, CheckCircle } from 'lucide-react'; // Added CheckCircle
import { unsubscribeUser } from '../services/authApi';

export default function Unsubscribe() {
  // 1. Initialize state for the success message and navigation
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', ibanLast5: '' });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Shared styling for form inputs updated to the new dark theme
  const inputClass = "w-full px-5 py-4 bg-[#0F172A] border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB] transition-all outline-none shadow-inner";
  const labelClass = "block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 mb-2.5";

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'First name is required.';
    if (!form.lastName.trim()) errs.lastName = 'Last name is required.';
    if (!form.email.trim()) errs.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Enter a valid email address.';
    if (!/^\d{5}$/.test(form.ibanLast5)) errs.ibanLast5 = 'Enter exactly 5 digits.';
    return errs;
  };

  // 2. Handle the form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    setApiError('');

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsLoading(true);

    try {
      await unsubscribeUser({
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim(),
        iban_last_5: form.ibanLast5,
        is_unsubscribe: true,
      });

      setIsSubmitted(true); // Show success message

      // Wait 3 seconds, then redirect to home
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      setApiError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen font-sans text-white bg-[#0F172A] relative">

      {/* GLOBAL BACKGROUND GLOWS */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1639322537228-f710d846310a?auto=format&fit=crop&q=80&w=2400"
          alt="Background"
          className="w-full h-full object-cover opacity-[0.03] mix-blend-screen"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/80 via-[#0F172A]/95 to-[#0F172A]" />
        {/* Updated Glows: Blue & Pink */}
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#2563EB]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-[#F43F5E]/10 rounded-full blur-[150px]" />
      </div>

      {/* HEADER */}
      <div className="relative z-50">
        <Header />
      </div>

      {/* MAIN CONTENT - Side-by-Side Split Layout */}
      <main className="flex-1 flex flex-col items-center justify-center w-full px-4 relative z-10 py-20 text-left pt-40">

        <div className="w-full max-w-5xl bg-[#1E293B]/90 backdrop-blur-2xl rounded-[2.5rem] border border-slate-700/80 shadow-2xl flex flex-col lg:flex-row overflow-hidden relative">

          {/* Subtle top border highlight */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-[#2563EB] opacity-80 z-20" />

          {/* LEFT PANE: Information & Context */}
          <div className="w-full lg:w-5/12 bg-[#151E2E] p-6 md:p-12 lg:p-16 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-slate-700/80 relative overflow-hidden">

            {/* Background decorative element */}
            <div className="absolute -left-12 -top-12 w-48 h-48 bg-[#2563EB]/10 rounded-full blur-[50px] pointer-events-none" />

            <div className="w-16 h-16 rounded-full bg-[#2563EB]/10 border border-[#2563EB]/20 flex items-center justify-center mb-8 shadow-sm relative z-10">
              <UserMinus className="w-8 h-8 text-[#2563EB]" strokeWidth={1.5} />
            </div>

            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white mb-6 relative z-10">
              Cancel <br /> Subscription
            </h1>

            <div className="space-y-6 text-slate-400 text-sm leading-relaxed relative z-10">
              <p>
                We're sorry to see you go. By filling out this form, your subscription will be cancelled immediately, and you will not be charged for the next billing cycle.
              </p>

              <div className="p-5 rounded-xl bg-[#2563EB]/10 border border-[#2563EB]/20 text-blue-100 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 shrink-0 text-[#2563EB] mt-0.5" />
                <p className="text-xs leading-relaxed">
                  If you need assistance or have questions about your account, please reach out to <a href="mailto:support@financialskillhub.net" className="text-[#2563EB] font-bold hover:underline">support@financialskillhub.net</a> before cancelling.
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT PANE: The Form or Success Message */}
          <div className="w-full lg:w-7/12 p-6 md:p-12 lg:p-16 bg-[#1E293B] flex flex-col justify-center">
            
            {/* 3. Conditional Rendering based on state */}
            {isSubmitted ? (
              // --- SUCCESS MESSAGE UI ---
              <div className="flex flex-col items-center justify-center text-center space-y-4 animate-[fadeIn_0.5s_ease-in-out]">
                <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-2 shadow-[0_0_30px_rgba(16,185,129,0.1)]">
                  <CheckCircle className="w-10 h-10 text-emerald-400" />
                </div>
                <h2 className="text-3xl font-bold text-white">Cancellation Confirmed</h2>
                <p className="text-slate-400 max-w-md">
                  Your request has been processed. We've cancelled your subscription and sent a confirmation to your email.
                </p>
                <div className="pt-6 flex items-center gap-2 text-sm text-slate-500">
                  <div className="w-4 h-4 rounded-full border-2 border-slate-500 border-t-transparent animate-spin"></div>
                  Redirecting to home page...
                </div>
              </div>
            ) : (
              // --- ORIGINAL FORM UI ---
              <form className="space-y-6" onSubmit={handleSubmit}>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>First Name <span className="text-[#F43F5E]">*</span></label>
                    <input type="text" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className={inputClass} required />
                    {errors.firstName && <p className="text-[#F43F5E] text-xs mt-1.5">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Last Name <span className="text-[#F43F5E]">*</span></label>
                    <input type="text" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className={inputClass} required />
                    {errors.lastName && <p className="text-[#F43F5E] text-xs mt-1.5">{errors.lastName}</p>}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Email Address <span className="text-[#F43F5E]">*</span></label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} required />
                  {errors.email && <p className="text-[#F43F5E] text-xs mt-1.5">{errors.email}</p>}
                </div>

                <div>
                  <label className={labelClass}>Last 5 Digits of IBAN <span className="text-[#F43F5E]">*</span></label>
                  <input type="text" value={form.ibanLast5} onChange={(e) => setForm({ ...form, ibanLast5: e.target.value.replace(/\D/g, '') })} placeholder="e.g. 12345" maxLength={5} className={inputClass} required />
                  {errors.ibanLast5 && <p className="text-[#F43F5E] text-xs mt-1.5">{errors.ibanLast5}</p>}
                </div>

                {apiError && <p className="text-[#F43F5E] text-sm">{apiError}</p>}

                <div className="pt-8 flex flex-col sm:flex-row gap-4">
                  {/* CONSTANT SECONDARY BUTTON STYLE (Left) */}
                  <Link
                    to="/"
                    className="bg-white/5 backdrop-blur-xl border border-slate-700 text-white px-8 py-4 rounded-2xl font-bold hover:bg-white/10 hover:border-slate-600 transition-all text-base flex items-center justify-center"
                  >
                    Keep me subscribed
                  </Link>
                  
                  {/* CONSTANT PRIMARY BUTTON STYLE (Right) */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 bg-[#2563EB] text-white px-8 py-4 rounded-xl text-base font-bold hover:bg-[#1d4ed8] hover:shadow-[0_0_15px_rgba(37,99,235,0.4)] transition-all text-center disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Cancelling...' : 'Confirm Cancellation'}
                  </button>
                </div>

              </form>
            )}
          </div>

        </div>

      </main>

      <div className="relative mt-auto">
        <Footer />
      </div>
    </div>
  );
}
